import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Button, Container, Pill, Text } from '@/components/ui';
import { useTheme } from '@/theme';

export function Hero() {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <View style={{ paddingTop: 48, paddingBottom: 64 }}>
      <Container>
        <View style={styles.wrap}>
          <View style={styles.copy}>
            <Pill variant="ghost">{t('home.hero.eyebrow')}</Pill>

            <Text variant="display1" style={[styles.title, { color: theme.colors.fg }]}>
              {t('home.hero.title')}{'\n'}
              <Text
                variant="display1"
                italic
                style={{
                  color: theme.colors.accent,
                }}
              >
                {t('home.hero.titleItalic')}
              </Text>
            </Text>

            <Text variant="bodyLarge" tone="fgSoft" style={styles.lede}>
              {t('home.hero.lede')}
            </Text>

            <View style={styles.actions}>
              <Button variant="primary" size="lg" trailingArrow href="/kontakt">
                {t('home.hero.primaryCta')}
              </Button>
              <Button variant="ghost" size="lg" href="/kurse">
                {t('home.hero.ghostCta')}
              </Button>
            </View>
          </View>

          <View
            style={[
              styles.media,
              {
                backgroundColor: theme.colors.bgSoft,
                borderRadius: theme.radii.md,
                borderColor: theme.colors.rule,
              },
            ]}
          >
            <StripedMedia
              accent={theme.colors.accent}
              accentAlt={theme.colors.accentAlt}
            />
            <Text variant="meta" tone="fgMute" style={styles.mediaCaption}>
              Tanzstudio Silbando · Zürich-West
            </Text>
          </View>
        </View>

        <View style={[styles.meta, { borderTopColor: theme.colors.rule }]}>
          <MetaItem label={t('home.hero.metaNext')} value={t('home.hero.metaNextValue')} />
          <MetaItem label={t('home.hero.metaStudio')} value={t('home.hero.metaStudioValue')} />
          <MetaItem label={t('home.hero.metaLanguage')} value={t('home.hero.metaLanguageValue')} />
          <MetaItem label={t('home.hero.metaCommunity')} value={t('home.hero.metaCommunityValue')} />
        </View>
      </Container>
    </View>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.metaItem}>
      <Text variant="eyebrow" tone="fgMute">{label}</Text>
      <Text variant="h4" style={{ marginTop: 6 }}>{value}</Text>
    </View>
  );
}

function StripedMedia({ accent, accentAlt }: { accent: string; accentAlt: string }) {
  const webGradient = {
    backgroundImage: `repeating-linear-gradient(45deg, ${accent} 0, ${accent} 14px, ${accentAlt} 14px, ${accentAlt} 28px)`,
  } as Record<string, string>;
  return (
    <View
      style={{
        aspectRatio: 4 / 5,
        width: '100%',
        backgroundColor: accent,
        overflow: 'hidden',
      }}
    >
      <View
        style={[
          {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
          webGradient as any,
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 48,
    alignItems: 'center',
  },
  copy: {
    flex: 1,
    minWidth: 320,
    gap: 24,
  },
  title: {
    marginTop: 8,
    fontSize: 96,
    lineHeight: 96,
  },
  lede: {
    maxWidth: 540,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  media: {
    flexBasis: 360,
    flexGrow: 0,
    flexShrink: 1,
    overflow: 'hidden',
    borderWidth: 1,
  },
  mediaCaption: {
    padding: 16,
  },
  meta: {
    marginTop: 64,
    paddingTop: 32,
    borderTopWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 32,
  },
  metaItem: {
    flexBasis: 200,
    flexGrow: 1,
  },
});
